import { Box, CircularProgress, Grid } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { selectedProjectAtom } from 'atoms';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { useAtomValue } from 'jotai';
import { FC, useEffect, useRef, useState } from 'react';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';
import { SIDE_PANEL_WIDTH } from 'utils/constants';
import ActivityFeedHeader from './components/header';
import PostLikersListModal from './components/likers';
import Post from './components/post';
import UserPost from './components/post-items/user-post/UserPost';
import { ContentWrapper } from './helpers/content-wrapper';
import { styles } from './styles/styles';

export interface IActivityFeedProps {
  posts: any;
  mentionOptions: ICustomMentionData[];
  searchQuery: string;
  showTabs?: boolean;
  uploadPath?: string;
  onCreatePost?: (body: Partial<IPost>) => void;
  onCreateComment?: (body: Partial<IPostComment>, parentPath: string) => void;
  onUpdatePost?: (body: Partial<IPost>) => void;
  onUpdateComment?: (body: Partial<IPostComment>) => void;
  onDeletePost?: (id: string) => void;
  onDeleteComment?: (id: string) => void;
  onTogglePostLike?: (id: string) => void;
  onSetSearchQuery: (query: string) => void;
}

const PADDING_X = 20;

const ActivityFeed: FC<IActivityFeedProps> = ({
  posts,
  mentionOptions,
  searchQuery,
  showTabs = false,
  uploadPath,
  onCreatePost,
  onCreateComment,
  onUpdatePost,
  onUpdateComment,
  onDeletePost,
  onDeleteComment,
  onTogglePostLike,
  onSetSearchQuery,
}) => {
  const { companyId } = useAppSelector(authSelector);
  const selectedProject = useAtomValue(selectedProjectAtom);
  const { data: userMe } = useGetMeQuery();

  const parentRef = useRef<HTMLDivElement | null>(null);

  const [isExpanded, setIsExpanded] = useState(false);

  const virtualizer = useVirtualizer({
    count: posts?.length || 0,
    estimateSize: () => 200,
    getScrollElement: () => parentRef.current,
    getItemKey: (index) => posts?.[index]?.id || index,
    overscan: 5,
  });

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    if (posts && posts.length > 0) {
      virtualizer.measure();
    }
  }, [posts, virtualizer]);

  const isEmpty = !posts?.length;

  const handleExpand = (expanded: boolean) => {
    setIsExpanded(expanded);
  };

  if (!selectedProject) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          width: SIDE_PANEL_WIDTH,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid sx={styles.container}>
      <ActivityFeedHeader
        toggleActivityFeedContent={() => {}}
        onSetSearchQuery={onSetSearchQuery}
        resetFilters={() => {}}
        searchQuery={searchQuery}
        showTabs={showTabs}
      />

      <Grid
        ref={parentRef}
        sx={{
          ...styles.mainContainer,
          ...(isExpanded && { marginBottom: '-22px' }),
        }}
      >
        <ContentWrapper isEmpty={isEmpty} isError={false} isLoading={false}>
          <Grid
            sx={{
              width: `calc(100% - ${2 * PADDING_X - 5}px)`,
              height: virtualizer.getTotalSize(),
              position: 'relative',
            }}
          >
            <Grid
              sx={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                transform: `translateY(${items[0]?.start ?? 0}px)`,
              }}
            >
              {items.map(({ index, key }) => {
                const post = posts?.[index];
                if (!post) return null;

                const isFirst = index === 0;
                return (
                  <Grid
                    key={key}
                    data-index={index}
                    ref={virtualizer.measureElement}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      paddingBottom: '20px',
                      borderBottom: '1px solid #EDEFF7',
                      marginTop: isFirst ? '0' : '20px',
                    }}
                  >
                    <UserPost
                      projectId={selectedProject.id}
                      post={post}
                      placeOfUse="activity_feed"
                      mentionOptions={mentionOptions}
                      searchQuery={searchQuery}
                      onCreateComment={(
                        body: Partial<IPostComment>,
                        parentPath: string = `${post.parentPath}/${post.id}`
                      ) => onCreateComment(body, parentPath)}
                      onUpdateComment={onUpdateComment}
                      onDeletePost={onDeletePost}
                      onUpdatePost={onUpdatePost}
                      onDeleteComment={onDeleteComment}
                      onTogglePostLike={() => onTogglePostLike(post.id)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </ContentWrapper>
      </Grid>

      {companyId && userMe && (
        <Grid sx={styles.createPostContainer}>
          <Post
            isExpanded={isExpanded}
            projectId={selectedProject.id}
            companyId={companyId}
            actionType="CREATE_POST"
            mentionOptions={mentionOptions}
            onExpand={handleExpand}
            onCreatePost={onCreatePost}
            onUpdatePost={onUpdatePost}
            setOpen={setIsExpanded}
            uploadPath={uploadPath}
          />
        </Grid>
      )}
      <PostLikersListModal />
    </Grid>
  );
};

export default ActivityFeed;
